import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Key,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import GastifyBrand from '../components/public/GastifyBrand';
import '../components/public/publicSite.css';
import { traducirErrorAuth } from '../utils/authErrors';
import { useAuth } from '../context/AuthContext';

const EMAIL_VALIDATION = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const fetchUserSecurityRecord = async (userId) => {
  for (const tableName of ['profiles', 'users']) {
    try {
      const { data } = await supabase
        .from(tableName)
        .select('requires_pw_update')
        .eq('id', userId)
        .single();

      if (data) return data;
    } catch {}
  }

  return null;
};

const clearPasswordResetFlag = async (userId) => {
  for (const tableName of ['profiles', 'users']) {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ requires_pw_update: false })
        .eq('id', userId);

      if (!error) return true;
    } catch {}
  }

  return false;
};

const getAuthFeedback = (error) => {
  const rawMessage = String(error?.message || '').trim();
  const message = rawMessage.toLowerCase();

  if (
    message.includes('invalid login credentials') ||
    message.includes('invalid credentials') ||
    message.includes('invalid email or password')
  ) {
    return {
      title: 'No pudimos iniciar sesión',
      message: 'El correo o la contraseña no coinciden. Revisá los datos e intentá nuevamente.',
    };
  }

  if (message.includes('email not confirmed') || message.includes('confirm')) {
    return {
      title: 'Cuenta pendiente de validación',
      message: 'La cuenta todavía no está confirmada. Revisá tu correo o consultá con el administrador.',
    };
  }

  if (message.includes('too many requests') || message.includes('rate limit')) {
    return {
      title: 'Demasiados intentos',
      message: 'Se hicieron varios intentos seguidos. Conviene esperar un momento antes de volver a probar.',
    };
  }

  if (message.includes('fetch') || message.includes('network') || message.includes('failed to fetch')) {
    return {
      title: 'Problema de conexión',
      message: 'No fue posible comunicarse con el servidor. Revisá la conexión e intentá nuevamente.',
    };
  }

  // Fallback: NUNCA devolver `rawMessage` crudo. Supabase responde en ingles
  // ("Password should be at least 6 characters") y eso terminaba impreso tal
  // cual sobre una UI en español. El traductor compartido cubre el resto de
  // los casos y, si tampoco los reconoce, da un generico en español.
  return {
    title: 'Error al iniciar sesión',
    message: traducirErrorAuth(error, 'Ocurrió un problema al validar las credenciales. Intentá nuevamente.'),
  };
};

export default function Login() {
  const { user: authUser, loading: authLoading } = useAuth();
  // 'login' | 'set-password' | 'forgot'
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authFeedback, setAuthFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  const navigate = useNavigate();

  // Entrar a /login con la sesion viva mostraba igual el formulario, lo que
  // reforzaba la sensacion de "me deslogueo" al recargar. Se excluye
  // 'set-password': ahi el usuario ESTA autenticado a proposito, terminando de
  // cambiar la contraseña provisoria, y sacarlo cortaria el flujo a la mitad.
  useEffect(() => {
    if (authLoading || view === 'set-password') return;
    if (authUser) navigate('/sistema', { replace: true });
  }, [authUser, authLoading, view, navigate]);


  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setAuthFeedback(null);

    if (!email || !EMAIL_VALIDATION.test(email)) {
      const feedback = {
        type: 'error',
        title: 'Email inválido',
        message: 'Ingresá un email válido para enviarte el link de recuperación.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/login/reset`,
      });
      if (error) throw error;

      setAuthFeedback({
        type: 'success',
        title: 'Mail enviado',
        message: 'Si la cuenta existe, te llega un mail con el link para definir nueva contraseña.',
      });
      toast.success('Te mandamos un mail con instrucciones');
    } catch (err) {
      const feedback = {
        type: 'error',
        title: 'No pudimos enviar el mail',
        message: traducirErrorAuth(err, 'Intentalo de nuevo en unos minutos.'),
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const clearFeedback = () => {
    if (authFeedback) setAuthFeedback(null);
  };

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setAuthFeedback(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) throw error;

      try {
        const profile = await fetchUserSecurityRecord(data.user.id);

        if (profile?.requires_pw_update) {
          setView('set-password');
          setAuthFeedback({
            type: 'success',
            title: 'Primer ingreso detectado',
            message: 'Antes de continuar, configurá la contraseña definitiva.',
          });
          toast('Configurá la contraseña definitiva', { icon: '🔐' });
          return;
        }
      } catch {
        // Sin bloqueo si la marca no existe en este entorno.
      }

      sessionStorage.removeItem('hasSeenStatus');
      toast.success('Bienvenido a Gastify');
      navigate('/sistema');
    } catch (error) {
      const feedback = getAuthFeedback(error);
      setAuthFeedback({ type: 'error', ...feedback });
      toast.error(feedback.message, { id: 'login-error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (event) => {
    event.preventDefault();
    setAuthFeedback(null);

    if (newPassword !== confirmPassword) {
      const feedback = {
        type: 'error',
        title: 'Contraseñas distintas',
        message: 'La confirmación no coincide con la nueva contraseña.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    if (newPassword.length < 6) {
      const feedback = {
        type: 'error',
        title: 'Contraseña muy corta',
        message: 'La nueva contraseña debe tener al menos 6 caracteres.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    if (newPassword === password) {
      const feedback = {
        type: 'error',
        title: 'Contraseña repetida',
        message: 'La nueva contraseña debe ser diferente a la provisoria.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.updateUser({ password: newPassword });

      if (authError) {
        if (authError.status === 422) {
          throw new Error('La contraseña no cumple los requisitos o es igual a la anterior.');
        }
        throw authError;
      }

      try {
        const currentUser = (await supabase.auth.getUser()).data.user;
        if (currentUser) {
          await clearPasswordResetFlag(currentUser.id);
        }
      } catch {
        // Sin bloqueo si la columna no existe.
      }

      toast.success('Contraseña actualizada correctamente');
      navigate('/sistema');
    } catch (error) {
      const feedback = {
        type: 'error',
        title: 'No pudimos actualizar la contraseña',
        message: traducirErrorAuth(error, 'Ocurrió un problema al guardar la nueva contraseña.'),
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit =
    view === 'login' ? handleLogin : view === 'forgot' ? handleForgotPassword : handleSetNewPassword;

  const heading =
    view === 'login'
      ? 'Ingresar a Gastify'
      : view === 'forgot'
      ? 'Recuperar contraseña'
      : 'Definí tu contraseña';
  const subheading =
    view === 'login'
      ? 'Accedé a tu cuenta para seguir gestionando tu negocio.'
      : view === 'forgot'
      ? 'Ingresá tu email y te mandamos un link para definir una nueva contraseña.'
      : 'Antes de continuar, configurá una contraseña definitiva para proteger el acceso.';

  return (
    <div className="gst-site gst-auth">
          <div className="auth-split">
            <div className="auth-panel-left">
              <a onClick={() => navigate('/')} style={{ display: 'flex', cursor: 'pointer' }}>
                <GastifyBrand textTone="light" iconSize={46} subtitle="Operación, caja y rentabilidad" />
              </a>
              <div>
                <h2>Gestioná sin complicarte.</h2>
                <p>Ventas, stock, compras y caja en una sola plataforma. Ingresá y seguí donde dejaste tu operación.</p>
              </div>
              <div className="quote">“Dejamos de perseguir planillas. Ahora sé cuánto tengo en caja antes de cerrar el local.” — Verónica, kiosco y minimercado</div>
            </div>

            <div className="auth-panel-right">
              <div className="auth-topbar">
                <button type="button" className="gst-btn gst-btn-ghost gst-btn-sm" onClick={() => navigate('/')}>
                  Volver a la web
                </button>
                {installPrompt ? (
                  <button type="button" className="gst-btn gst-btn-primary gst-btn-sm" onClick={handleInstallClick}>
                    Instalar app
                  </button>
                ) : (
                  <div className="hint">¿No tenés cuenta? <Link to="/registro-onboarding">Creá una gratis</Link></div>
                )}
              </div>

              <div className="auth-form-wrap">
                <div className="auth-brand-mobile">
                  <GastifyBrand textTone="dark" iconSize={42} subtitle={null} />
                </div>
                <h1>{heading}</h1>
                <div className="sub">{subheading}</div>

                {authFeedback && (
                  <div
                    className="gst-field"
                    style={{
                      flexDirection: 'row',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '12px 14px',
                      borderRadius: 12,
                      marginBottom: 16,
                      border: `1px solid ${authFeedback.type === 'success' ? 'var(--gst-mint-200)' : '#FECACA'}`,
                      background: authFeedback.type === 'success' ? 'var(--gst-mint-100)' : '#FEE2E2',
                      color: authFeedback.type === 'success' ? 'var(--gst-mint-700)' : '#991B1B',
                    }}
                  >
                    {authFeedback.type === 'success' ? <CheckCircle2 size={18} style={{ marginTop: 1, flexShrink: 0 }} /> : <AlertCircle size={18} style={{ marginTop: 1, flexShrink: 0 }} />}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.16em', textTransform: 'uppercase' }}>{authFeedback.title}</div>
                      <p style={{ marginTop: 2, fontSize: 13, lineHeight: 1.5 }}>{authFeedback.message}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
                  {view !== 'set-password' && (
                    <div className="gst-field">
                      <label className="gst-label">Email</label>
                      <div style={{ position: 'relative' }}>
                        <Mail size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gst-graphite-500)', pointerEvents: 'none' }} />
                        <input
                          className="gst-input"
                          style={{ paddingLeft: 38 }}
                          type="email"
                          required
                          placeholder="vos@tunegocio.com"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); clearFeedback(); }}
                        />
                      </div>
                    </div>
                  )}

                  {view === 'login' && (
                    <div className="gst-field">
                      <label className="gst-label">Contraseña</label>
                      <div style={{ position: 'relative' }}>
                        <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gst-graphite-500)', pointerEvents: 'none' }} />
                        <input
                          className="gst-input"
                          style={{ paddingLeft: 38, paddingRight: 38 }}
                          type={showPass ? 'text' : 'password'}
                          required
                          placeholder="Tu contraseña"
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); clearFeedback(); }}
                        />
                        <button type="button" onClick={() => setShowPass((v) => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gst-graphite-700)' }}>
                          {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {view === 'set-password' && (
                    <>
                      <div className="gst-field">
                        <label className="gst-label">Nueva contraseña</label>
                        <div style={{ position: 'relative' }}>
                          <Key size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gst-graphite-500)', pointerEvents: 'none' }} />
                          <input className="gst-input" style={{ paddingLeft: 38 }} type="password" required placeholder="Mínimo 6 caracteres" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); clearFeedback(); }} />
                        </div>
                      </div>
                      <div className="gst-field">
                        <label className="gst-label">Confirmar contraseña</label>
                        <div style={{ position: 'relative' }}>
                          <ShieldCheck size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gst-graphite-500)', pointerEvents: 'none' }} />
                          <input className="gst-input" style={{ paddingLeft: 38 }} type="password" required placeholder="Repetí la contraseña" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); clearFeedback(); }} />
                        </div>
                      </div>
                    </>
                  )}

                  {view === 'login' && (
                    <div className="row-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                      <label className="gst-checkbox-row" style={{ alignItems: 'center' }}>
                        <input type="checkbox" /> Recordarme
                      </label>
                      <button type="button" onClick={() => { clearFeedback(); setView('forgot'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: 'var(--gst-mint-700)' }}>
                        Olvidé mi contraseña
                      </button>
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="gst-btn gst-btn-primary gst-btn-block" style={{ padding: 16, opacity: loading ? 0.7 : 1 }}>
                    {loading ? (<><Loader2 size={16} className="animate-spin" /> Procesando</>) : (
                      <>
                        {view === 'login' ? 'Ingresar' : view === 'forgot' ? 'Enviar instrucciones' : 'Actualizar acceso'}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  {view === 'forgot' && (
                    <button type="button" onClick={() => { clearFeedback(); setView('login'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: 'var(--gst-graphite-700)', textAlign: 'center' }}>
                      ← Volver al login
                    </button>
                  )}
                </form>

                {view === 'login' && (
                  <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--gst-graphite-700)', marginTop: 24 }}>
                    ¿No tenés cuenta? <Link to="/registro-onboarding" style={{ fontWeight: 800 }}>Creá una gratis</Link>
                  </p>
                )}

                <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--gst-graphite-700)', marginTop: 16, lineHeight: 1.5 }}>
                  Al continuar, aceptás los{' '}
                  <Link to="/legales/terminos" style={{ fontWeight: 800, color: 'var(--gst-graphite-900)' }}>Términos</Link>{' '}y la{' '}
                  <Link to="/legales/privacidad" style={{ fontWeight: 800, color: 'var(--gst-graphite-900)' }}>Política de Privacidad</Link>.
                </p>
              </div>
            </div>
          </div>
    </div>
  );
}
